// eslint-disable-next-line import/no-cycle
import store from '@/store';
import { camelizeKeys } from 'humps';
import isSetSeats from 'utils/purchase/validateSetSeat';
import { isPetFriendlySeat, isSeatOccupied } from './purchase/seats';
import {
  getBfoPurchaseWays,
  getResultPricingVariant,
  isBfoSupportedJourney,
} from './purchase/baseFareOverride';
import growthBook from '../services/growthBook';
import { isOverbookingEligible } from './overbooking';
import { getSeatPriceZonesFromSource } from './seatPricing';
import { legFromFragments, roundTripFareApplies } from './roundTripFare';

const LS_DEPARTURE_SEATS_EXPIRATION = 60 * 60 * 1000; // 1 hour
const LS_DEPARTURE_SEATS = 'reservamos-selectedSeats';

const isPlainObject = (value) => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const countPassengerRecords = (passengers) => {
  if (passengers === undefined || passengers === null) return 0;
  return Array.isArray(passengers) ? passengers.length : null;
};

/**
 * Keeps persistence geometry tied to a proven layout family.
 * Unknown values deliberately disable bus/van position inference.
 * @param {*} value Candidate diagram family.
 * @returns {'bus'|'van'|'unknown'} Supported capture family.
 */
export const getSeatPreferenceDiagramType = (value) =>
  ['bus', 'van'].includes(value) ? value : 'unknown';

/**
 * Resolves the authoritative booking count used only by explicit preference capture.
 * A malformed nonempty category map fails closed instead of falling back to records.
 * @param {object} params Booking count sources.
 * @param {object|Array|null} params.passengerSelection Ordinary category count map.
 * @param {Array<object>} params.passengers Created or selected passenger records.
 * @param {boolean} params.isExchange Whether records are the selected exchange subset.
 * @returns {number|null} Safe authoritative count, or null for malformed input.
 */
export const getBookingPassengerCount = ({
  passengerSelection,
  passengers,
  isExchange = false,
} = {}) => {
  const recordCount = countPassengerRecords(passengers);
  if (isExchange) return recordCount;

  if (
    passengerSelection === undefined ||
    passengerSelection === null ||
    (Array.isArray(passengerSelection) && passengerSelection.length === 0) ||
    (isPlainObject(passengerSelection) && Object.keys(passengerSelection).length === 0)
  ) {
    return recordCount;
  }

  if (!isPlainObject(passengerSelection)) return null;

  let total = 0;
  const counts = Object.values(passengerSelection);
  let index = 0;
  while (index < counts.length) {
    const rawCount = counts[index];
    index += 1;
    let count = null;
    if (typeof rawCount === 'number') {
      count = rawCount;
    } else if (typeof rawCount === 'string' && /^\d+$/.test(rawCount.trim())) {
      count = Number(rawCount.trim());
    }

    if (!Number.isSafeInteger(count) || count < 0 || !Number.isSafeInteger(total + count)) {
      return null;
    }
    total += count;
  }

  return total;
};

/**
 * Get the pricing for a trip.
 * @param {Object} params - The parameters object.
 * @param {Object} params.trip - The trip object.
 * @param {string} params.way - The way of the trip.
 * @param {boolean} params.isRound - Whether the trip is round or not.
 * @param {boolean} [params.earnsRoundTripFare] - Whether the return leg earns the round-trip fare.
 * @returns {Object|undefined} - The pricing object for the trip.
 */
export const getTripPricing = ({ trip, way, isRound, earnsRoundTripFare = true }) => {
  if (isRound) {
    if (way === 'departure') return trip?.departureRoundTripPricing;

    // Defaults to true so callers that cannot see the departure leg keep quoting as before.
    return earnsRoundTripFare ? trip?.roundTripPricing : trip?.pricing;
  }
  return trip?.pricing;
};

/**
 * Get the total price from a trip's pricing.
 * @param {Object} params - The parameters object.
 * @param {Object} params.trip - The trip object.
 * @param {string} params.way - The way of the trip.
 * @param {boolean} params.isRoundTrip - Whether the trip is round or not.
 * @returns {number} - The total price, or 0 if not found.
 */
export const getTripTotal = ({ trip, way, isRoundTrip }) => {
  const pricing = getTripPricing({ trip, way, isRound: isRoundTrip });

  return Number(pricing?.total) || 0;
};

/**
 * Saves the selected seats on local storage.
 *
 * Persisting is best effort: the browser throws on setItem when the origin has its site
 * data blocked or its quota exhausted. This runs inside the seats reducer
 * (reducers/seatsBus.js, SELECT_SEATS_TRIPS) *before* the new state is produced, so letting
 * the error bubble aborts the dispatch and the seat selection is lost with no feedback on
 * screen. Swallowing it keeps the selection working and only gives up the restore on reload,
 * which is what its reader getSeatsFromLS already does.
 * @param {Object} params - The params object containing the id and seats.
 * @param {string} params.id - The id of the trip.
 * @param {array} params.seats - The selected seats.
 */
export const saveSeatsOnLS = ({ id, seats }) => {
  try {
    const newExpiration = Date.now() + LS_DEPARTURE_SEATS_EXPIRATION;
    const lsSeats = { id, seats, expiresAt: newExpiration };
    localStorage.setItem(LS_DEPARTURE_SEATS, JSON.stringify(lsSeats));
  } catch (error) {
    // Nothing to recover: the selection lives in the store, local storage is only a cache.
  }
};

/**
 * Retrieves the selected seats from local storage.
 * @param {string} id - The id of the trip.
 * @returns {array} - The selected seats.
 */
export const getSeatsFromLS = ({ id }) => {
  try {
    const lsSeats = localStorage.getItem(LS_DEPARTURE_SEATS);
    if (!lsSeats) return null;

    const { id: lsId, seats, expiresAt } = JSON.parse(lsSeats);

    const isExpired = Date.now() > expiresAt;
    if (isExpired || lsId !== id) {
      localStorage.removeItem(LS_DEPARTURE_SEATS);
      return null;
    }
    return seats || [];
  } catch (error) {
    return [];
  }
};

/**
 * Get the selected seats from the seats state or local storage.
 * @param {Object} params - The parameters object.
 * @param {Object} params.seatsState - The seats state object.
 * @param {string} params.fromId - The id of the trip.
 * @param {boolean} params.isFromOpen - The open ticket status of the trip.
 * @returns {array} - The selected seats.
 */
export const getFromSelectedSeats = ({ seatsState, fromId, isFromOpen }) => {
  if (isFromOpen) return [];
  let fromSelectedSeats = seatsState[fromId]?.selectedSeats?.filter(
    (seat) => !seat.isPickedAsAdjacent,
  );
  if (!fromSelectedSeats) {
    fromSelectedSeats = getSeatsFromLS({ id: fromId });
  }
  return fromSelectedSeats || [];
};

/**
 * Get the props needed when a trip is passed as a prop
 * @param {Object} params - The parameters object
 * @param {String} params.way - The way of the trip ('departure' or 'return')
 * @param {Object} params.trip - The trip object
 * @param {Object} params.fromTrip - The fromTrip object (for return trips)
 * @param {Object} [params.fromExchange=null] - Optional exchange data when auto-detection is not available
 * @param {boolean} [params.isRoundTrip=false] - Explicit round-trip fact from the result card; it
 *   selects departure/return round-trip pricing and, for a canonical BFO leg, the request variant.
 * @returns {Object} - The seat selector props object
 *
 * @example
 * // Normal usage (auto-detects exchange from store)
 * getSeatSelectorPropsFromTrip({ way: 'departure', trip, fromTrip })
 *
 * // With explicit exchange data (fallback for edge cases)
 * getSeatSelectorPropsFromTrip({
 *   way: 'departure',
 *   trip,
 *   fromTrip,
 *   fromExchange: { passengers: [...] }
 * })
 */
export const getSeatSelectorPropsFromTrip = ({
  way = 'departure',
  trip,
  fromTrip,
  fromExchange = null,
  isRoundTrip = false,
}) => {
  const state = store.getState();
  const seatsBus = state.seatsBus.toJS();
  const { features, env } = state.whitelabelConfig || {};

  const isExchange = Boolean(fromExchange);
  const pricingVariant = getResultPricingVariant({
    pricing: getTripPricing({ trip, way, isRound: isRoundTrip }),
    way,
    isRoundTrip,
    features,
    brand: env?.brand,
    isExchange,
    isOpenTicket: Boolean(trip.openTicket),
    hasConnections: Boolean(trip.hasConnections),
  });

  // Validate and extract exchange data
  const exchangePassengers = fromExchange?.passengers || [];
  const validPassengers =
    Array.isArray(exchangePassengers) && exchangePassengers.length > 0 ? exchangePassengers : [];
  const searchPassengerSelection = state.search?.get
    ? state.search.get('passengers')
    : state.search?.passengers;
  const bookingPassengerCount = getBookingPassengerCount({
    passengerSelection: searchPassengerSelection?.toJS?.() ?? searchPassengerSelection,
    passengers: isExchange ? validPassengers : [],
    isExchange,
  });

  // A cached result map is only usable for the pricing context it was loaded for; while a
  // different context is requested nothing from the previous quote is exposed.
  const cachedEntry = seatsBus[trip.id];
  const isSameContext =
    Boolean(cachedEntry) && (cachedEntry.pricingVariant ?? undefined) === pricingVariant;
  const {
    layout: seatsLayout,
    seats,
    busCategories,
    selectedSeats,
    error,
    seatPriceZones: seatPriceZonesFromState,
    tripPricingFields: tripPricingFieldsFromDetails,
    passengerTypes: passengerTypesFromDetails,
    diagramType: seatMapDiagramType,
  } = isSameContext ? cachedEntry : {};
  const { allowsSeatSelection, openTicket, isPetFriendly } = trip;
  const tripAllowsSeatSelection = allowsSeatSelection && !openTicket;
  /*
   * Fragments are create based on the way trip, both are created because the component needed like that.
   * But only one of them will have a value, the other will be an empty array.
   */
  let departureFragments = [];
  let returnFragments = [];
  let minPassengers = 0;

  const tripWithDetailsPricing = { ...trip, ...tripPricingFieldsFromDetails };
  const tripPricing = getTripPricing({
    trip: tripWithDetailsPricing,
    way,
    isRound: isRoundTrip,
  });
  const seatPriceZones =
    (Array.isArray(seatPriceZonesFromState) && seatPriceZonesFromState.length > 0
      ? seatPriceZonesFromState
      : getSeatPriceZonesFromSource(trip)) || [];
  const passengerTypes =
    (Array.isArray(passengerTypesFromDetails) && passengerTypesFromDetails.length > 0
      ? passengerTypesFromDetails
      : trip.passengerTypes) || [];

  if (way === 'departure') {
    departureFragments = [trip];
    // Set minPassengers for exchange departures
    minPassengers = isExchange ? validPassengers.length : 0;
  }

  let departureSeats;
  if (way === 'return') {
    /**
     *  Empty value to just fetch the return trip
     */
    departureFragments = [];
    returnFragments = [trip];
    departureSeats = getFromSelectedSeats({
      seatsState: seatsBus,
      fromId: fromTrip.id,
      isFromOpen: fromTrip.openTicket,
    });
    minPassengers = fromTrip.openTicket ? 0 : departureSeats?.length || 0;
  }

  const purchaseDepartureTrip = departureFragments[0];
  const purchaseReturnTrip = returnFragments[0] || {};

  const [firstFloorReclination, secondFloorReclination] =
    (way === 'departure'
      ? purchaseDepartureTrip.seatsRecline
      : purchaseReturnTrip.seatsRecline
    )?.split('/') || [];

  const isOverbooking = isOverbookingEligible(trip);

  return {
    tripId: trip.id,
    hasSeats: Boolean(seatsLayout?.length && seats && Object.keys(seats).length),
    departureFragments,
    returnFragments,
    hasDetails: false,
    isFetchingDetails: false,
    passengers: isExchange ? validPassengers : [],
    bookingPassengerCount,
    isExchange,
    adjacentSeatPolicy: 'disabled', // Adjacent seats not allowed on exchange
    busCategories: busCategories || [],
    isLockingTickets: false,
    fetchingDetails: false,
    departureSelectedSeats: departureSeats,
    selectedSeats: selectedSeats || [],
    seatPriceZones,
    layout: seatsLayout || [],
    lockedSeats: [],
    isPetFriendlyTrip: isPetFriendly,
    seats,
    diagramType: 'bus',
    seatPreferenceDiagramType: getSeatPreferenceDiagramType(seatMapDiagramType),
    isOpenTicket: openTicket,
    isLoading: false,
    minPassengers,
    allowsSeatSelection: tripAllowsSeatSelection,
    canResetPassengers: false,
    canGoBack: false,
    isPurchaseView: false,
    tripPricing,
    pricingVariant,
    trip,
    fetchingDetailsError: error,
    passengerTypes,
    firstFloorReclination,
    secondFloorReclination: secondFloorReclination || firstFloorReclination,
    overbooking: isOverbooking,
  };
};

/**
 * Get the props needed when a purchase is passed as a prop.
 * @param {Object} params - The parameters object.
 * @param {Object} params.purchase - The purchase object.
 * @param {string} params.way - The way of the purchase.
 * @returns {Object} - The props needed for the purchase.
 */
export const getSeatSelectorPropsFromPurchase = ({ purchase, way = 'departure' }) => {
  const {
    departs,
    returns,
    hasDetails,
    isFetchingDetails,
    passengers,
    passengerSelection,
    isExchange,
    busCategories,
    isUpdating,
    roundTrip,
  } = purchase;
  const bookingPassengerCount = getBookingPassengerCount({
    passengerSelection,
    passengers,
    isExchange,
  });
  const seatsBus = store.getState().seatsBus.toJS();

  const { fragments: departureFragments, diagramType } = departs || {};
  const { fragments: returnFragments = [] } = returns || {};
  const departTripSlug = departs.tripSlug;
  const returnTripSlug = returns.tripSlug;
  const purchaseDepartureTrip = departureFragments[0];
  const { tickets } = purchaseDepartureTrip;
  const isOpenDepartureTicket = purchaseDepartureTrip.openTicket;
  const lockedDepartureSeats = tickets?.map((ticket) => ticket.seat);

  const purchaseReturnTrip = returnFragments[0] || {};
  const tripId = way === 'departure' ? purchaseDepartureTrip.id : purchaseReturnTrip.id;
  const { tickets: returnTickets } = purchaseReturnTrip || [];
  const isOpenReturnTicket = purchaseReturnTrip.openTicket;
  const lockedReturnSeats = returnTickets?.map((ticket) => ticket.seat);

  const lockedSeats = way === 'departure' ? lockedDepartureSeats : lockedReturnSeats;

  const isOpenTicket = way === 'departure' ? isOpenDepartureTicket : isOpenReturnTicket;

  const isLockingTickets =
    way === 'departure' ? departs?.isLockingTickets : returns?.isLockingTickets;
  const fetchingDetails = way === 'departure' ? departs?.fetchingDetails : returns?.fetchingDetails;
  const selectedSeats = way === 'departure' ? departs?.selectedSeats : returns?.selectedSeats;
  let seatPreferenceDiagramType = getSeatPreferenceDiagramType(
    way === 'departure' ? departs?.diagramType : returns?.diagramType,
  );
  const seatPriceZones = way === 'departure' ? departs?.seatPriceZones : returns?.seatPriceZones;
  let seats = way === 'departure' ? departs?.seats : returns?.seats;
  let layout = way === 'departure' ? departs?.layout : returns?.layout;
  const { features, env } = store.getState().whitelabelConfig;
  const purchaseWay = way === 'departure' ? 'departs' : 'returns';
  const bfoPurchaseWays = isBfoSupportedJourney({ purchase, features, brand: env?.brand })
    ? [...new Set([...getBfoPurchaseWays(purchase), ...(purchase.bfoReselectionWays || [])])]
    : [];
  const usePerLegBfoDetails = bfoPurchaseWays.length > 0;
  const useBfoPurchaseScope = bfoPurchaseWays.includes(purchaseWay);
  const hasResultsSeatMap = isSetSeats(seatsBus, [tripId], { [tripId]: undefined });
  if (!useBfoPurchaseScope && hasResultsSeatMap) {
    layout = seatsBus[tripId].layout;
    seats = seatsBus[tripId].seats;
    seatPreferenceDiagramType = getSeatPreferenceDiagramType(seatsBus[tripId].diagramType);
  }

  const isPetFriendlyTrip =
    way === 'departure' ? purchaseDepartureTrip.isPetFriendly : purchaseReturnTrip.isPetFriendly;

  let minPassengers = isExchange ? passengers.length : 0;
  if (way === 'return') {
    minPassengers = purchaseDepartureTrip.openTicket
      ? 0
      : passengers.length ||
        purchase.preSelectedSeats?.departure?.length ||
        departs?.selectedSeats?.length ||
        0;
  }

  const isOverbookingDeparture = isOverbookingEligible(purchaseDepartureTrip);
  const isOverbookingReturn = isOverbookingEligible(purchaseReturnTrip);

  const departureCanSelectSeats =
    !purchaseDepartureTrip.openTicket &&
    purchaseDepartureTrip.allowsSeatSelection &&
    !isOverbookingDeparture;

  const returnCanSelectSeats =
    purchaseReturnTrip.allowsSeatSelection && !isOpenTicket && !isOverbookingReturn;

  const allowsSeatSelection = way === 'departure' ? departureCanSelectSeats : returnCanSelectSeats;
  const canGoBack = way === 'return' && departureCanSelectSeats;

  const canResetPassengers = departureCanSelectSeats || returnCanSelectSeats;

  // Without this the seat preview kept quoting the round-trip fare while the sidebar beside it
  // already quoted the single one, leaving two prices for the same leg on one screen.
  const { features: whitelabelFeatures } = store.getState().whitelabelConfig;
  const earnsRoundTripFare =
    !whitelabelFeatures?.VALIDATE_ROUND_TRIP_DISCOUNT ||
    roundTripFareApplies({
      departureLeg: legFromFragments(departureFragments),
      returnLeg: legFromFragments(returnFragments),
    });

  const tripPricing = getTripPricing({
    trip: way === 'departure' ? purchaseDepartureTrip : purchaseReturnTrip,
    way,
    isRound: Boolean(purchaseReturnTrip),
    earnsRoundTripFare,
  });

  const hasAdjacentSeatPassenger = passengers?.some(
    (passenger) => passenger.wantsOutgoingAdjacentSeat,
  );

  // Determine adjacent seat exchange mode based on feature flags
  let adjacentSeatPolicy = 'disabled';
  if (isExchange && hasAdjacentSeatPassenger) {
    if (features.EXCHANGE_ALLOW_PASSENGER_ADJACENT_SEAT && features.EXCHANGE_ALLOW_ADJACENT_SEAT) {
      adjacentSeatPolicy = 'primary_and_adjacent';
    } else if (features.EXCHANGE_ALLOW_PASSENGER_ADJACENT_SEAT) {
      adjacentSeatPolicy = 'primary_only';
    }
  }
  const hasSeats = Boolean(layout?.length && seats && Object.keys(seats).length);
  const isLoading = !allowsSeatSelection || isLockingTickets || !hasSeats || isUpdating;

  const [firstFloorReclination, secondFloorReclination] =
    (way === 'departure'
      ? purchaseDepartureTrip.seatsRecline
      : purchaseReturnTrip.seatsRecline
    )?.split('/') || [];

  const currentWay = purchase[purchaseWay] || {};
  const currentHasDetails = usePerLegBfoDetails
    ? Boolean(currentWay.hasDetails || (!useBfoPurchaseScope && hasResultsSeatMap))
    : hasDetails;
  const currentIsFetchingDetails = usePerLegBfoDetails
    ? Boolean(currentWay.fetchingDetails)
    : isFetchingDetails;

  return {
    tripId,
    hasSeats,
    purchase,
    useBfoPurchaseScope,
    usePerLegBfoDetails,
    departureFragments,
    returnFragments,
    hasDetails: currentHasDetails,
    isFetchingDetails: currentIsFetchingDetails,
    passengers,
    bookingPassengerCount,
    isExchange,
    hasAdjacentSeatPassenger,
    adjacentSeatPolicy,
    busCategories,
    isLockingTickets,
    fetchingDetails,
    selectedSeats,
    seatPriceZones,
    layout,
    lockedSeats,
    isPetFriendlyTrip,
    seats,
    diagramType,
    seatPreferenceDiagramType,
    isOpenTicket,
    isLoading,
    minPassengers,
    canGoBack,
    allowsSeatSelection,
    canResetPassengers,
    isPurchaseView: true,
    tripPricing,
    departTripSlug,
    returnTripSlug,
    firstFloorReclination,
    secondFloorReclination: secondFloorReclination || firstFloorReclination,
    isRoundTrip: roundTrip,
    trip: way === 'departure' ? purchaseDepartureTrip : purchaseReturnTrip,
  };
};

/**
 * Checks if a reservation has selected seats or if the seat selection is not allowed.
 * @param {Object} reservation - The reservation object.
 * @returns {boolean} - Returns true if the reservation has selected seats or seat selection is not allowed.
 */
export const reservationHasSelectedSeats = (reservation) => {
  const seats = reservation.get('selectedSeats');
  const hasSeats = Boolean(seats && seats.size > 0);
  const allowsSeatSelection =
    reservation.get('transportType') === 'bus' &&
    reservation.getIn(['fragments', 0, 'line', 'allowsSeatSelection']);

  return !allowsSeatSelection || hasSeats;
};

/**
 * Avoid to select petFriendly as first selected seat
 * If first is pet friendly and there are non pet friendly seats then swap
 * @param {array} seats
 * @returns {array}
 */
export const preventSelectPetFriendly = (seats) => {
  if (seats && seats.length > 1) {
    if (seats.some(({ category }) => isPetFriendlySeat(category))) {
      const nonSpecialSeatPosition = seats.findIndex(
        ({ category }) => !isPetFriendlySeat(category),
      );
      if (nonSpecialSeatPosition > 0) {
        const deleted = seats.splice(nonSpecialSeatPosition, 1);
        seats = [...deleted, ...seats];
      }
    }
  }
  return seats;
};

/**
 * Categorizes the seats into occupied and free.
 * @param {Object} params - function params.
 * @param {Object} params.seats - The seats object.
 * @param {Array} params.selectedSeats - The selected seats.
 * @returns {Object} seatsCount - An object containing the categorized seats.
 * @returns {Number} seatsCount.occupied - The number of occupied seats.
 * @returns {Number} seatsCount.free - The number of free seats.
 * @returns {Number} seatsCount.selected - The number of selected seats.
 */
export const getSeatsCount = ({ seats = {}, selectedSeats = [] }) => {
  const seatsCounted = Object.keys(seats).reduce(
    (result, seatNumber) => {
      const seat = seats[seatNumber];
      if (isSeatOccupied(seat)) result.occupied += 1;
      else if (seat.occupied === false) result.available += 1;
      return result;
    },
    { occupied: 0, available: 0 },
  );

  return {
    ...seatsCounted,
    selected: selectedSeats.length,
  };
};

/**
 * Updates a seat in the selected seats array.
 * @param {Object} options - The options object.
 * @param {Array} options.selectedSeats - The array of selected seats.
 * @param {Object} options.seat - The seat object to update.
 * @returns {Array} - The updated array of selected seats.
 */
export const getUpdatedSeats = ({ selectedSeats, seat }) => {
  const selectedSeatsCopy = [...selectedSeats];
  const index = selectedSeatsCopy.findIndex((selectedSeat) => selectedSeat.number === seat.number);
  selectedSeatsCopy[index] = seat;
  return selectedSeatsCopy;
};

/**
 * Retrieves the purchase payload for selected seats.
 * @param {Object} options - The options object.
 * @param {string} options.departureId - The departure ID.
 * @param {string} options.returnId - The return ID.
 * @returns {Object|null} - The purchase payload for selected seats, or null if no seats are selected.
 */
export const getSeatsPurchasePayload = ({ departureId, returnId }) => {
  const seatsBus = store.getState().seatsBus.toJS();
  const departureSeats =
    seatsBus[departureId]?.selectedSeats || getSeatsFromLS({ id: departureId }) || [];
  const returnSeats = seatsBus[returnId]?.selectedSeats || [];

  if (!departureSeats?.length && !returnSeats?.length) return null;

  return camelizeKeys({
    preSelectedSeats: {
      departure: departureSeats,
      return: returnSeats,
    },
  });
};

// the selected seats are filter to only take the ones have a seat number.
/**
 * Retrieves the seats for a specific way of a purchase.
 * @param {Object} rawPurchase - The raw purchase object.
 * @param {string} way - The way (e.g., 'departs', 'returns').
 * @returns {Array} - The array of seats for the specified way.
 */
export const getTicketsSeats = (rawPurchase, way) => {
  const ticketsFromFragments =
    rawPurchase[way]?.fragments
      ?.reduce((acc, fragment) => {
        const fragmentTickets = fragment.tickets || [];
        return [...acc, ...fragmentTickets];
      }, [])
      ?.filter((ticket) => /\d/.test(ticket.seat))
      .map((ticket) => {
        return {
          number: String(ticket.seat),
          category: ticket.category,
          seatLevel: ticket.seatLevel,
          seatFloor: ticket.seatFloor,
          seatFareDescription: ticket.seatFareDescription,
          adjacentSeats: {
            numbers: [ticket.isAdjacentSeat ? ticket.parentAdjacentSeat : ticket.adjacentSeat],
            // TODO For price we need to search on the other tickets
            // price
          },
          ...(ticket.adjacentSeat && { isAdjacentPicked: true }),
          ...(ticket.isAdjacentSeat && { isPickedAsAdjacent: true }),
          tripSlug: ticket.tripSlug || '',
        };
      }) ?? [];
  return ticketsFromFragments;
};

/**
 * Get the selected seats for a purchase.
 * @param {Object} options - The options object.
 * @param {Object} options.purchaseData - The purchase data.
 * @param {Object} options.currentSelectedSeats - The current selected seats.
 * @returns {Object} - The selected seats for departure and return.
 */
export const getPurchaseSelectedSeats = ({ purchaseData, currentSelectedSeats }) => {
  const departureWaySeats = getTicketsSeats(purchaseData, 'departs') || [];
  const returnWaySeats = getTicketsSeats(purchaseData, 'returns') || [];

  /**
   * Merges seat data from current and new selections to retain existing seat details.
   * @param {Array} existingSeats - The array of seats currently selected with detailed information.
   * @param {Array} newSeats - The array of new seats to be merged, potentially lacking full details.
   * @returns {Array} - An array of seats with merged information, combining existing and new seat data.
   */
  const mergeSeatData = (existingSeats, newSeats) => {
    return newSeats.map((newSeat) => {
      const matchedSeat = existingSeats.find(
        (seat) => String(seat.number) === String(newSeat.number),
      );
      return matchedSeat ? { ...matchedSeat, ...newSeat } : newSeat;
    });
  };

  const selectedDepartureSeats = mergeSeatData(currentSelectedSeats.depart, departureWaySeats);
  const selectedReturnSeats = mergeSeatData(currentSelectedSeats.return, returnWaySeats);

  const preSelectedSeats = purchaseData.preSelectedSeats || {};

  return {
    departure: selectedDepartureSeats.length
      ? selectedDepartureSeats
      : preSelectedSeats.departure || [],
    return: selectedReturnSeats.length ? selectedReturnSeats : preSelectedSeats.return || [],
  };
};

/**
 * Checks if the seats are shown in results.
 * @returns {Boolean}
 */
export const isSeatsOnResultActivated = () => {
  const { features } = store.getState().whitelabelConfig;
  const { departure } = store.getState().search.toJS();
  const resultsSeats = features.RESULTS_SEATS;
  const resultsSimple = features.ENABLE_CATEGORIES;
  const isBulkTicket = departure?.routeType === 'bulk_ticket';

  // TODO Temporary AB Test for personalization
  const disablePersonalization = growthBook.getGrowthBookFeature('disable_personalization');

  // TO DO: remove the growthBook experiment
  return (
    !disablePersonalization &&
    resultsSeats &&
    features.SEATS_ON_RESULTS &&
    (resultsSimple !== 'control' ?? true) &&
    !isBulkTicket
  );
};

/**
 * Checks if the category  is enabled .
 * @returns {Boolean}
 */
export const isPreviewCategoriesEnabled = () => {
  const { features } = store.getState().whitelabelConfig;
  const resultsSimple = features.ENABLE_CATEGORIES;
  if (!resultsSimple) return true;
  return Boolean(resultsSimple === 'categories');
};

// These functions are now imported from adjacentCard.js

/**
 * Determines whether the user should be redirected to the seat selection page.
 *
 * @param {Object} params - The function parameters.
 * @param {boolean} params.useSeatsBasedPricing - Whether seat-based pricing is used.
 * @param {Object} params.purchase - The purchase data.
 * @returns {boolean} Whether the user should be redirected to the seat selection page.
 */
export const shouldRedirectToSeatSelection = ({ useSeatsBasedPricing, purchase }) => {
  // Update parameters
  const { departs, returns } = purchase;

  // Only applies for seats with pricing, if is not enabled redirect is false
  if (!useSeatsBasedPricing) {
    return false;
  }

  const departureLayout = departs?.seats || {};
  const returnLayout = returns?.seats || {};
  const layoutHasPrices =
    Object.values(departureLayout).some((s) => s?.price != null) ||
    Object.values(returnLayout).some((s) => s?.price != null);

  if (!layoutHasPrices) return false;

  // If both flags are active and we have a purchase with selected seats
  const departureSeats = departs?.selectedSeats || [];
  const returnSeats = returns?.selectedSeats || [];

  // Check if any selected seats are missing prices
  const hasSeatsWithoutPrices = [...departureSeats, ...returnSeats].some((seat) => !seat.price);

  return hasSeatsWithoutPrices;
};
